import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDefaultRange } from './api';
import type {
  SummaryMode,
  SummaryQualityFilters,
  SummaryQualityState,
  SummaryQualityTab,
  TimelineBucket,
} from './types';

const TABS: SummaryQualityTab[] = ['edit-rate', 'latency', 'regeneration', 'verification', 'audio'];

const normalizeMode = (value: string | null): SummaryMode | null =>
  value === 'conversation' || value === 'dictation' ? value : null;

const normalizeTab = (value: string | null): SummaryQualityTab => TABS.find((tab) => tab === value) ?? 'edit-rate';

const normalizeBucket = (value: string | null): TimelineBucket => (value === 'day' ? 'day' : 'week');

const emptyFilters = (): SummaryQualityFilters => ({
  ...getDefaultRange(),
  orgId: null,
  accountId: null,
  model: null,
  promptHash: null,
  mode: null,
  templateId: null,
  includeBackfilled: true,
});

export const readInitialState = (): SummaryQualityState => {
  const params = new URLSearchParams(window.location.search);
  const defaults = emptyFilters();
  return {
    filters: {
      ...defaults,
      from: params.get('from') || defaults.from,
      to: params.get('to') || defaults.to,
      model: params.get('model'),
      promptHash: params.get('prompt_hash'),
      mode: normalizeMode(params.get('mode')),
      templateId: params.get('template_id'),
      includeBackfilled: params.get('include_backfilled') !== 'false',
    },
    tab: normalizeTab(params.get('tab')),
    bucket: normalizeBucket(params.get('bucket')),
  };
};

// org/account filters are auth ids, so they live in component state only and never
// reach the address bar (history, referrers, screenshots). The URL is rebuilt from
// scratch below using only these entries, so an identity id that arrives on the URL
// (e.g. a pasted link) is dropped rather than preserved across the next write.
const writeUrl = ({ filters, tab, bucket }: SummaryQualityState) => {
  const params = new URLSearchParams();
  const entries: [string, string | null][] = [
    ['from', filters.from],
    ['to', filters.to],
    ['model', filters.model],
    ['prompt_hash', filters.promptHash],
    ['mode', filters.mode],
    ['template_id', filters.templateId],
    ['include_backfilled', filters.includeBackfilled ? null : 'false'],
    ['tab', tab === 'edit-rate' ? null : tab],
    ['bucket', bucket === 'week' ? null : bucket],
  ];
  for (const [key, value] of entries) {
    if (value) params.set(key, value);
  }
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
};

export const useSummaryQualityFilters = () => {
  const [state, setState] = useState(readInitialState);

  useEffect(() => writeUrl(state), [state]);

  const setFilter = useCallback(
    (filterPatch: Partial<SummaryQualityFilters>) =>
      setState((current) => ({ ...current, filters: { ...current.filters, ...filterPatch } })),
    []
  );
  const setTab = useCallback((tab: SummaryQualityTab) => setState((current) => ({ ...current, tab })), []);
  const setBucket = useCallback((bucket: TimelineBucket) => setState((current) => ({ ...current, bucket })), []);
  const clearFilters = useCallback(() => setState((current) => ({ ...current, filters: emptyFilters() })), []);

  const hasActiveFilters = useMemo(() => {
    const { filters } = state;
    return Boolean(
      filters.orgId ||
        filters.accountId ||
        filters.model ||
        filters.promptHash ||
        filters.mode ||
        filters.templateId ||
        !filters.includeBackfilled
    );
  }, [state]);

  return { ...state, hasActiveFilters, setFilter, setTab, setBucket, clearFilters };
};
